import { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import Button, { ButtonProps } from '.';
import { joinNames } from '../helpers';
import style from './style.module.scss';
import { useFormikContext } from 'formik';

type ButtonAction = { button: string };
type Action = 'replace' | 'submit' | 'click' | 'dismissable' | 'ignore' | 'reload';
export type ActionPayload = { action: Action } & (
    | { action: 'dismissable' }
    | (ButtonAction & { action: 'submit'; formName: string })
    | (ButtonAction & { action: 'replace'; url: string })
    | (ButtonAction & { action: 'click'; onClick: () => void })
    | { action: 'ignore' }
    | { action: 'reload' }
);

export function isButtonAction(action: ActionPayload | ButtonAction): action is ButtonAction {
    return typeof (action as ButtonAction).button == 'string';
}

type ActionButtonProps = { action: ActionPayload; afterActionHandler: () => void };

export function ActionButton({ action, afterActionHandler, ...props }: ActionButtonProps & Omit<ButtonProps, 'onClick' | 'disabled'>) {
    const onClick: MouseEventHandler<HTMLButtonElement> = useMemo(() => {
        switch (action.action) {
            case 'replace':
                return (e) => {
                    e.stopPropagation();
                    setTimeout(afterActionHandler, 0);
                    location.replace(action.url || './');
                };

            case 'submit':
                return (e) => {
                    e.stopPropagation();
                    setTimeout(afterActionHandler, 0);
                    const form = document.forms.namedItem(action.formName);
                    if (form) {
                        form.requestSubmit();
                    }
                };

            case 'click':
                return (e) => {
                    e.stopPropagation();
                    setTimeout(afterActionHandler, 0);
                    action.onClick();
                };

            default:
                return (e) => {
                    e.stopPropagation();
                    setTimeout(afterActionHandler, 0);
                    console.log(action);
                };
        }
    }, [action, afterActionHandler]);
    return action.action != 'dismissable' ? (
        <Button onClick={onClick} type="button" {...props}>
            {isButtonAction(action) ? action.button : action.action}
        </Button>
    ) : null;
}

type BusyButtonProps = { execute: () => Promise<void>; text: string; busyText?: string } & Omit<ButtonProps, 'onClick' | 'children'>;
/**
 * A button that shows busy-state, and disables onClick while busy executing the execute-function.
 * Errors are re-thrown and should be handled on parent component.
 * @param {BusyButtonProps['execute']} props.execute An async function to execute on click
 */
export function SubmitButton({ execute, text, busyText, disabled: disabledProp, className, ...props }: BusyButtonProps) {
    busyText = busyText || text;
    const [executing, setExecuting] = useState(false);
    const onClick = useCallback(async () => {
        if (!executing) {
            setExecuting(true);
            try {
                await execute();
                setExecuting(false);
            } catch (err) {
                setExecuting(false);
                throw err;
            }
        }
    }, [execute, executing]);

    return (
        <Button
            onClick={onClick}
            disabled={disabledProp || executing}
            className={joinNames(className, executing ? style.executing : undefined)}
            {...props}
        >
            <span className="icon"></span>
            {executing ? busyText : text}
        </Button>
    );
}
type FormikSubmitButtonProps = { text: string; busyText?: string; disabled?: boolean } & Omit<ButtonProps, 'onClick' | 'children'>;
export function FormikSubmitButton({ text, busyText, disabled: disabledProp, className, ...props }: FormikSubmitButtonProps) {
    const { isSubmitting } = useFormikContext();
    busyText = busyText || text;
    return (
        <Button
            type="submit"
            disabled={disabledProp || isSubmitting}
            className={joinNames(className, isSubmitting ? style.executing : undefined)}
            {...props}
        >
            <span className="icon"></span>
            {isSubmitting ? busyText : text}
        </Button>
    );
}
